import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowBigLeft } from 'lucide-react';
import ErrorPage from '@/components/ErrorPage';
import { useAuth } from '@/contexts/AuthContext';
import { AUTH_LOGIN } from '@/constants/constant-routes';

export default function AccessDenied() {
  const { logout, refreshPermissions } = useAuth();
  const navigate = useNavigate();
  const [goingBack, setGoingBack] = useState(false);

  // Always land on login — whether the logout API succeeds or fails
  // (e.g., no token → 401), the session is cleared client-side and we navigate.
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed, redirecting to login anyway:', error);
    } finally {
      window.history.replaceState(null, '', AUTH_LOGIN);
      window.location.replace(AUTH_LOGIN);
    }
  };

  /**
   * Re-read permissions before routing. Landing here can mean the cache was
   * empty rather than the user unauthorised, and with an empty cache every
   * branch of SmartRedirect leads back to this page — so going home without
   * refreshing first just returns the learner to where they started.
   */
  const handleBack = async () => {
    if (goingBack) return;
    setGoingBack(true);
    try {
      await refreshPermissions();
    } finally {
      setGoingBack(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <ErrorPage
      title="403"
      subtitle="Access Denied"
      description="Looks like you don't have the required permissions to access this page. Please check your credentials or contact your administrator."
      primaryButtonText="Back"
      primaryButtonIcon={ArrowBigLeft}
      onPrimaryClick={handleBack}
      secondaryButtonText="Logout"
      secondaryButtonIcon={Lock}
      onSecondaryClick={handleLogout}
      busy={goingBack}
    />
  );
}
